interface FreshnessFooterSlotProps {
  /** ISO timestamp of `max(last_analysis_at)` across in-scope repos. Null when
   *  no analysis exists. */
  freshness: string | null;
}

/**
 * Single-line page footer reading "Sonar last analysed {humanised} ago".
 * US2 ships with a placeholder; US3 (PRODOPSAI-331) wires real freshness from
 * the summary endpoint. Tooltip surfaces the absolute UTC timestamp.
 */
export function FreshnessFooterSlot({ freshness }: Readonly<FreshnessFooterSlotProps>) {
  if (freshness === null) {
    return (
      <p
        className="mt-2 text-[10px] italic text-muted-foreground"
        data-testid="code-quality-freshness-footer"
      >
        Sonar last analysed —
      </p>
    );
  }
  return (
    <p
      className="mt-2 text-[10px] italic text-muted-foreground"
      title={freshness}
      data-testid="code-quality-freshness-footer"
    >
      Sonar last analysed {humaniseRelative(freshness)} ago
    </p>
  );
}

/**
 * Tiny humaniser — "X minutes / hours / days ago". Kept inline so US3 doesn't
 * have to introduce a date-fns dependency or hunt for an existing helper.
 * Falls back to the absolute date when the value is older than ~30 days.
 */
function humaniseRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const ms = Math.max(0, now - then);
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  const days = Math.round(hours / 24);
  if (days <= 30) return `${days} ${days === 1 ? 'day' : 'days'}`;
  return `on ${iso.slice(0, 10)}`;
}
